/** The sidepanel views */

import { Conflict } from '@/types/Conflicten';
import { Defect } from '@/types/Defecten';

import { Reserve } from '../types';
import { DDPNotitie, DDPNotitieTable } from '../types/ddpTypes';

/** Create a sidepanel view, a sidepanel view always has at least 1 tab. */
export type SidepanelViews = 'defect' | 'conflict' | 'reserve' | 'voorraad' | undefined;
export type Tabs = 'Notitie' | 'Staat' | 'Voorraad' | 'VoorraadRegionaal';

/** Map a view to a tab or multiple tabs. */
export const sidepanelTabs = {
  defect: ['Notitie', 'Staat'], // 'Notitie'
  conflict: ['Notitie'], // 'Notitie'
  reserve: ['Notitie'], // 'Notitie'
  voorraad: ['Voorraad'],
};

/** The main sidepanel title based on the current view. */
export const sidepanelTabTitle = {
  defect: 'materieel',
  conflict: 'conflict',
  reserve: 'materieel',
  voorraad: 'voorraad',
};

/**
 * Get the sublabel for a given column.
 */
export const getSublabel = {
  MANCO: 'Manco',
  VERKEERD_MATERIEEL: 'Verkeerd mat',
  BEPERKING_IN_TREINDIENST: 'Defect beperkt inzetbaar',
  DEFECT_NIET_INZETBAAR: 'Defect niet inzetbaar',
  GEEN_BEPERKINGEN: 'Defect inzetbaar',
  RESERVE: 'Reserve',
  undefined: '',
};

/**
 * DDP Notities
 */

export const ddpStatusLabel = {
  '': 'Geen status',
  NOTITIE_ORDER: 'Order',
  NOTITIE_ONDERHOUDSBOEK: 'Onderhoudsboek',
  NOTITIE_OP_SB_OB: 'Op SB/OB',
};

export const createEmptyNotitie = (): DDPNotitie =>
  structuredClone({
    relatedTable: undefined,
    relatedIdentifier: undefined,
    status: undefined,
    opmerking: undefined,
  });

/** Sidepanel views are connected to the underlaying entity, this maps the view tot the entity table name.
 *
 * @param view
 * @returns
 */
export const mapSidepanelViewToNotitieTable = (view: SidepanelViews): DDPNotitieTable | undefined => {
  if (!view) return;
  const viewToTable: { [key: string]: DDPNotitieTable } = {
    defect: 'DEFECTEN',
    conflict: 'CONFLICTEN',
    reserve: 'RESERVES',
  };
  return viewToTable[view];
};

/** Get the relatedTable and relatedIdentifier from the sidepanel store.
 *
 * @param view SidepanelViews
 * @param viewData Defect | Conflict | Reserve | undefined = undefined
 * @returns [DDPNotitieTable, string] | [undefined, undefined]
 */
export const mapSidepanelViewToNotitieIdentifier = (
  view: SidepanelViews,
  viewData: Defect | Conflict | Reserve | undefined = undefined,
): [DDPNotitieTable, string] | [undefined, undefined] => {
  const table = mapSidepanelViewToNotitieTable(view);
  if (!view || !table || viewData === undefined) return [undefined, undefined];
  if (table === 'DEFECTEN' && 'werkorderId' in (viewData as Defect)) {
    const defect = viewData as Defect;
    return [table, String(defect.werkorderId)];
  }
  if (table === 'CONFLICTEN' && 'werklijnId' in (viewData as Conflict))
    return [table, (viewData as Conflict).werklijnId];

  if (table === 'RESERVES' && 'eenheid' in (viewData as Reserve)) return [table, (viewData as Reserve).eenheid];

  return [undefined, undefined];
};

/** Get the related fields of a DDP Notitie.
 *
 * @param view SidepanelViews
 * @param viewData Defect | Conflict | Reserve | undefined
 * @returns
 */
export const getRelatedNotitieFields = (
  view: SidepanelViews,
  viewData: Defect | Conflict | Reserve | undefined = undefined,
): Omit<DDPNotitie, 'status' | 'opmerking'> | undefined => {
  const [relatedTable, relatedIdentifier] = mapSidepanelViewToNotitieIdentifier(view, viewData);
  if (!relatedTable || !relatedIdentifier) return;
  return {
    relatedTable,
    relatedIdentifier,
  };
};
